import React from "react";
import { AA } from "@/constants/testIds";
import { ArrowRight, Crosshair } from "@phosphor-icons/react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1723107638733-16ef49e5d4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxkcm9uZSUyMGNvbnN0cnVjdGlvbiUyMHNpdGUlMjBhZXJpYWx8ZW58MHx8fHwxNzgwOTkyMDI3fDA&ixlib=rb-4.1.0&q=85";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A]">
      {/* Background image with ken-burns + overlay */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Aerial view of a construction site at dusk"
          className="kenburns w-full h-full object-cover opacity-65"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-transparent" />
        <div className="absolute inset-0 bp-grid opacity-40" />
      </div>

      {/* Scan-line */}
      <div className="absolute inset-x-0 top-0 bottom-0 pointer-events-none overflow-hidden">
        <div className="scan-line" />
      </div>

      {/* Top coordinate strip */}
      <div className="relative pt-24 pb-3 border-b border-white/5 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-8 pr-8">
              <span>LAT 39°57′09″N</span><span>LON 75°09′55″W</span>
              <span>DATUM · NAVD88</span><span>TOL · 1:500</span>
              <span>VA — MD — DE — NJ — PA — NY — CT — RI — MA — NH — VT — ME</span>
              <span>FAA PART 107</span><span>ALTA/NSPS</span>
              <span>LAT 39°57′09″N</span><span>LON 75°09′55″W</span>
              <span>DATUM · NAVD88</span><span>TOL · 1:500</span>
            </div>
          ))}
        </div>
      </div>

      {/* Hero content */}
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-28">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 rise" style={{ animationDelay: "120ms" }}>
            <div className="label-mono mb-6 flex items-center gap-2">
              <Crosshair size={14} weight="bold" /> 01 / Precision Survey · Drone · Layout
            </div>
            <h1 className="font-display font-black text-white leading-[0.92] text-[44px] sm:text-[64px] lg:text-[92px] tracking-tighter">
              Aligned to the<br />
              <span className="text-[#FF4500]">millimeter.</span><br />
              Built to the sky.
            </h1>
            <p className="mt-7 text-white/70 max-w-2xl text-[17px] leading-relaxed">
              From core-hole laser plummets to LiDAR drone corridors, Aerial Alignment delivers
              construction layout, rail layout, conventional land surveying and drone services
              from <span className="text-white">Virginia to Maine</span> — held to NAVD88, ALTA/NSPS
              and FAA Part 107 standards.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <button
                data-testid={AA.heroCtaQuote}
                onClick={() => scrollTo("quote")}
                className="aa-btn aa-btn-primary btn-traced"
              >
                Generate Instant Quote <ArrowRight size={16} weight="bold" />
              </button>
              <button
                data-testid={AA.heroCtaServices}
                onClick={() => scrollTo("services")}
                className="aa-btn aa-btn-ghost"
              >
                Explore Capabilities
              </button>
            </div>
          </div>

          <div className="lg:col-span-4 rise" style={{ animationDelay: "260ms" }}>
            <div className="relative hairline corner-ticks bg-black/40 backdrop-blur-md p-6">
              <span className="ct-tr" /><span className="ct-bl" />
              <div className="label-mono mb-4">Field Brief / 24·7</div>
              <div className="space-y-5">
                <Stat k="States covered" v="11" sub="VA → ME" />
                <Stat k="Tolerance held" v="1 : 500" sub="Tower plumb spec" />
                <Stat k="Service categories" v="04" sub="Survey · Layout · Rail · Drone" />
                <Stat k="Datum" v="NAVD88" sub="Vertical reference" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom reticle bar */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A0A0A]/80 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-3 font-mono text-[10px] tracking-[0.25em] uppercase text-white/50">
          <span>Aerial Alignment / Est. East Coast</span>
          <span className="text-[#FF4500]">// SCROLL TO ACQUIRE TARGET ↓</span>
          <span>516.502.8172</span>
        </div>
      </div>
    </section>
  );
}

function Stat({ k, v, sub }) {
  return (
    <div className="border-t border-white/10 pt-4 first:border-t-0 first:pt-0">
      <div className="label-mono text-white/40 mb-1" style={{ color: "rgba(255,255,255,0.4)" }}>
        {k}
      </div>
      <div className="font-display font-black text-3xl text-white leading-none">{v}</div>
      <div className="font-mono text-[11px] text-white/50 mt-1">{sub}</div>
    </div>
  );
}
